import { DsAvatar, DsTypography } from '@ice-web-app/designSystem';
import { TIPS } from '@ice-web-app/shared-constants';
import { useAuth, useLanguage, useTranslation } from '@ice-web-app/shared-hooks';
import { useCurrentUserStore, useTourStore } from '@ice-web-app/shared-store';
import { routes } from '@ice-web-app/shared-ui';
import { Box, Stack } from '@mui/material';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import Fade from '@mui/material/Fade';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import type { MouseEvent, MouseEventHandler } from 'react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DsIcon, DsIconProps } from '../../../../components/icons/DsIcon';
import { useSettings } from '../../../@core/hooks/useSettings';
import { ChangeLanguageModal } from './ChangeLanguageModal';

const UserDropdownMenuItem = ({
  title,
  icon,
  onClick,
}: { title: string; icon: DsIconProps['icon']; onClick: MouseEventHandler<HTMLLIElement> }) => {
  return (
    <MenuItem sx={{ px: 3, py: 2, gap: 2, mb: 0.5 }} onClick={onClick}>
      <DsIcon size={22} icon={icon} />
      <DsTypography variant="body2" color="iceGray.700">
        {title}
      </DsTypography>
    </MenuItem>
  );
};

const UserDropdown = () => {
  const { t } = useTranslation();
  const { tourProps } = useTourStore();

  // Store
  const { currentUser } = useCurrentUserStore();

  // States
  const [open, setOpen] = useState(false);
  const [changeLanguageModalOpen, setChangeLanguageModalOpen] = useState(false);

  // Refs
  const anchorRef = useRef<HTMLDivElement>(null);

  // Hooks
  const router = useNavigate();
  const { logout } = useAuth();

  const { settings } = useSettings();

  const { currentLanguageDisplayName, currentLanguageIcon } = useLanguage();

  // get permissions - places where the user not have access to them
  /* useQuery(GET_NOT_ACCESS, {
    context: {
      uri: getEnvVariable('VITE_GRAPHQL_URL_GENERAL'),
    },
    onCompleted: (data) => {
      setUserNotAccessView(
        data?.getMyPortalDetails?.[0].accessPolicies
          .filter(({ action }) => action === AccessPolicyActionEnum.None)
          .map(({ name }) => name),
      );
      setUserNotAccessActions(
        data?.getMyPortalDetails?.[0].accessPolicies
          .filter(({ action }) => action === AccessPolicyActionEnum.View)
          .map(({ name }) => name),
      );
    },
  }); */

  // get user subscription
  /* useQuery(GET_SUBSCRIPTION, {
    context: {
      uri: getEnvVariable('VITE_GRAPHQL_URL_GENERAL'),
    },
    onCompleted: (data) => {
      setUserSubscription(data?.getMySubscriptionStatus);
    },
  }); */

  // Test exception errors that coms from the BE side
  /* useQuery(TEST_EXCEPTION, {
    variables: {
      input: false,
    },
    context: {
      uri: getEnvVariable('VITE_GRAPHQL_URL_GENERAL'),
    },
    onCompleted: (data) => {
      console.log(data);
    },
    onError: (error) => {
      graphErrorHandler(error);
    },
  }); */

  const handleDropdownOpen = () => {
    if (!open) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const handleDropdownClose = (event?: MouseEvent<HTMLLIElement> | (MouseEvent | TouchEvent), url?: string) => {
    if (url) {
      router(url);
    }

    if (anchorRef.current?.contains(event?.target as HTMLElement)) {
      return;
    }

    !tourProps.isOpen && setOpen(false);
  };

  const handleUserLogout = () => {
    logout();
  };

  return (
    <>
      <Stack
        ref={anchorRef}
        onClick={handleDropdownOpen}
        direction="row"
        data-tour={TIPS.LAYOUT.HEADER.PROFILE_ICON.DATA}
        justifyContent="center"
        alignItems="center"
        gap={2}
        sx={{ cursor: 'pointer' }}
      >
        <DsAvatar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          imgUrl={currentUser?.profilePicture}
          alt={currentUser ? `${currentUser?.firstName} ${currentUser?.lastName}` : '...'}
          size={44}
          badgeColor="success"
          badgeSize={8}
        />
        <DsIcon icon="CHEVRON_DOWN" size={16} />
      </Stack>
      <Popper
        open={open}
        data-tour={TIPS.LAYOUT.HEADER.PROFILE_ICON_DROPDOWN.DATA}
        transition
        disablePortal
        placement="bottom-end"
        anchorEl={anchorRef.current}
        sx={{ width: 272, height: 424, zIndex: 1 }}
        className="!mbs-1"
      >
        {({ TransitionProps, placement }) => (
          <Fade
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom-end' ? 'right top' : 'left top',
            }}
          >
            <Paper className={settings.skin === 'bordered' ? 'border shadow-none' : 'shadow-lg'}>
              <ClickAwayListener onClickAway={(e) => handleDropdownClose(e as MouseEvent | TouchEvent)}>
                <MenuList>
                  <Stack direction="row" sx={{ px: 5, py: 2, gap: 3 }} tabIndex={-1}>
                    <DsAvatar
                      imgUrl={currentUser?.profilePicture}
                      alt={currentUser ? `${currentUser?.firstName} ${currentUser?.lastName}` : '...'}
                      size={40}
                    />
                    <Stack>
                      <DsTypography variant="body1_500" color="iceGray.700">
                        {currentUser ? `${currentUser?.firstName} ${currentUser?.lastName}` : '...'}
                      </DsTypography>
                      <DsTypography variant="body2" color="iceGray.500">
                        Consultant
                      </DsTypography>
                    </Stack>
                  </Stack>

                  <Divider sx={{ mt: 1, mb: 2 }} />

                  <UserDropdownMenuItem
                    title={t('LABEL_SETTINGS', { label: t('ACCOUNT') })}
                    icon="SETTING_COG"
                    onClick={(e) => handleDropdownClose(e, routes.account + routes.accountSetting)}
                  />
                  <UserDropdownMenuItem
                    title={t('MY_LABEL', { label: t('MEMBERSHIP') })}
                    icon="PROFILE"
                    onClick={(e) => handleDropdownClose(e, routes.account + routes.accountMembership)}
                  />
                  <UserDropdownMenuItem
                    title={t('BILLING_AND_PLANS')}
                    icon="BILLS_PLANS"
                    onClick={handleDropdownClose}
                  />
                  <UserDropdownMenuItem
                    title={t('NOTIFICATIONS')}
                    icon="NOTIFICATION"
                    onClick={(e) => handleDropdownClose(e, routes.account + routes.accountNotification)}
                  />
                  <UserDropdownMenuItem title={t('CONNECTIONS')} icon="SHARE" onClick={handleDropdownClose} />
                  <UserDropdownMenuItem title={t('SECURITY')} icon="SECURITY" onClick={handleDropdownClose} />
                  <UserDropdownMenuItem
                    title={t('FAQ')}
                    icon="FAQ"
                    onClick={(e) => handleDropdownClose(e, routes.account + routes.accountFAQ)}
                  />
                  <UserDropdownMenuItem
                    title={currentLanguageDisplayName}
                    icon={currentLanguageIcon}
                    onClick={() => setChangeLanguageModalOpen(true)}
                  />

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="error"
                      size="small"
                      endIcon={<DsIcon icon="LOGOUT" />}
                      onClick={handleUserLogout}
                      sx={{ '& .MuiButton-endIcon': { marginInlineStart: 1.5 } }}
                    >
                      {t('LOGOUT')}
                    </Button>
                  </Box>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>

      <ChangeLanguageModal open={changeLanguageModalOpen} onClose={() => setChangeLanguageModalOpen(false)} />
    </>
  );
};

export default UserDropdown;
